import {
	dedupeAppIds,
	getGraphNodeAppId,
	type BylineAppId,
	type BylineProviderIdentity,
} from '../utils/bylineAppIdentity.ts';
import {
	extractIncomingLinkUrl,
	type IncomingLinkNodeData,
} from '../utils/extractIncomingLinkUrl.ts';

/** The slice of a `graphStore_cypherQueryV2` edge the app identity reads; both graph samples fit. */
export type GraphSampleEdge = {
	readonly node: {
		readonly columns: ReadonlyArray<{
			readonly value: {
				readonly __typename?: string;
				readonly id?: string;
				readonly data?: IncomingLinkNodeData | null;
			} | null;
		}>;
	};
};

// `provider` is selected on the `External*` members only, so the generated union does not expose
// it on every member; read it structurally.
const readProvider = (data: IncomingLinkNodeData): BylineProviderIdentity | null => {
	const provider = (data as { provider?: BylineProviderIdentity | null }).provider;
	return provider ?? null;
};

/**
 * The distinct apps a graph sample's hydrated rows identify, in result order. Rows AGG could not
 * hydrate (`data: null`) are skipped whatever their ARI says: the client drops a row with no URL,
 * so counting its app would reserve a tile the client never fills. They still count toward the row
 * minimum through the sample's `count`. CCINT-9543
 */
export const readGraphSampleAppIds = (edges: ReadonlyArray<GraphSampleEdge>): BylineAppId[] => {
	const appIds: Array<BylineAppId | null> = [];
	for (const edge of edges) {
		// Both samples return one column per row; the normalizers reject anything else.
		const value = edge.node.columns[0]?.value;
		if (value?.__typename !== 'GraphStoreCypherQueryV2AriNode' || !value.id) {
			continue;
		}
		const data = value.data;
		if (!data) {
			continue;
		}
		appIds.push(
			getGraphNodeAppId({
				ari: value.id,
				typename: data.__typename,
				provider: readProvider(data),
				url: extractIncomingLinkUrl(data),
			}),
		);
	}
	return dedupeAppIds(appIds);
};

export type GraphSampleRowCounts = Readonly<{
	total: number;
	/** Rows in the graph's own third-party ARI container, which hydrate through a connector. */
	thirdParty: number;
	/** Rows AGG returned without `data`. */
	unhydrated: number;
}>;

// Diagnoses Appears in latency: whether slow samples are the ones with many rows, or with many
// third-party rows to hydrate. CCINT-9673
export const readGraphSampleRowCounts = (
	edges: ReadonlyArray<GraphSampleEdge>,
): GraphSampleRowCounts => {
	let thirdParty = 0;
	let unhydrated = 0;
	for (const edge of edges) {
		const value = edge.node.columns[0]?.value;
		if (value?.id?.startsWith('ari:cloud:graph:')) {
			thirdParty += 1;
		}
		if (!value?.data) {
			unhydrated += 1;
		}
	}
	return { total: edges.length, thirdParty, unhydrated };
};
